/**
 * LeadsGlobalSearch — Oct 2026 · R294
 * Universal "Search customer" field above the subtab bar. Searches
 * every lead field + comment/history text via GET /leads?q=
 * Clicking a result opens the Edit Lead dialog.
 */
import { useEffect, useRef, useState } from "react";
import { Search, X as XIcon, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function LeadsGlobalSearch({ wsId, onPickLead }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const reqIdRef = useRef(0);

  // Close popup on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!wsId) return;
    const term = q.trim();
    if (term.length < 2) { setRows([]); return; }
    const id = ++reqIdRef.current;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/workspaces/${wsId}/leads`, {
          params: { q: term, lean: true, include_signals: false, limit: 20 },
        });
        if (id !== reqIdRef.current) return;
        setRows(Array.isArray(data) ? data.slice(0, 20) : (data?.leads || data?.rows || []).slice(0, 20));
        setOpen(true);
      } catch (e) {
        if (id === reqIdRef.current) setRows([]);
      } finally {
        if (id === reqIdRef.current) setBusy(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q, wsId]);

  const pick = (l) => {
    setOpen(false);
    setQ("");
    setRows([]);
    onPickLead?.(l);
  };

  const highlight = (text, term) => {
    if (!text || !term) return text || "";
    const idx = String(text).toLowerCase().indexOf(term.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {String(text).slice(0, idx)}
        <mark className="bg-amber-100 text-amber-900 rounded-sm px-0.5">
          {String(text).slice(idx, idx + term.length)}
        </mark>
        {String(text).slice(idx + term.length)}
      </>
    );
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="leads-global-search">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => { if (rows.length > 0) setOpen(true); }}
          placeholder="Search customers — name, email, phone, notes, follow-ups…"
          className="w-full h-10 pl-9 pr-9 text-sm border border-border bg-background rounded-none focus:outline-none focus:ring-2 focus:ring-primary/30"
          data-testid="leads-global-search-input"
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); setRows([]); setOpen(false); inputRef.current?.focus(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 inline-flex items-center justify-center text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
            data-testid="leads-global-search-clear"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XIcon className="w-3.5 h-3.5" />}
          </button>
        )}
        {busy && !q && (
          <Loader2 className="w-3.5 h-3.5 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        )}
      </div>
      {open && q.trim().length >= 2 && (
        <div
          className="absolute left-0 right-0 top-full mt-1 border border-border bg-background shadow-lg z-30 max-h-[70vh] overflow-y-auto"
          data-testid="leads-global-search-popup"
        >
          {rows.length === 0 && !busy && (
            <div className="p-4 text-xs text-muted-foreground text-center">
              No customers match &ldquo;{q}&rdquo;.
            </div>
          )}
          {rows.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => pick(l)}
              className="w-full text-left px-3 py-2 border-b border-border/50 hover:bg-muted/40 active:bg-muted/60 transition-colors flex items-start gap-2"
              data-testid={`leads-global-search-result-${l.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{highlight(l.name || "—", q)}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {l.company ? <>{highlight(l.company, q)}{l.title ? ` · ${l.title}` : ""}</> : (l.title || "—")}
                </div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {l.email ? <>{highlight(l.email, q)}</> : null}
                  {l.email && (l.phone || l.mobile) ? " · " : ""}
                  {(l.phone || l.mobile) ? highlight(l.phone || l.mobile, q) : null}
                </div>
              </div>
              <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-border bg-muted/40 whitespace-nowrap shrink-0">
                {l.status || "new"}
              </span>
            </button>
          ))}
          <div className="px-3 py-2 bg-muted/20 text-[10px] text-muted-foreground">
            Search covers name, company, email, phone, notes, follow-ups and comment history.
          </div>
        </div>
      )}
    </div>
  );
}
